import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Building2, ShieldCheck, IdCard } from "lucide-react"
import { formatCurrency, formatDate, LOCALE_MAP } from "@/lib/utils"
import { cn } from "@/lib/utils"
import type { AdminReservationRow } from "@fohet/caravan-booker-shared/types"
import { ReservationDetailModal } from "@/components/admin/ReservationDetailModal"

// Aggregate a per-guest boolean flag across a reservation's guests into a
// tri-state: "all" (every guest true), "some" (at least one), "none".
type FlagState = "all" | "some" | "none"
const aggregateFlag = (guests: AdminReservationRow["guests"], pred: (g: AdminReservationRow["guests"][number]) => boolean): FlagState => {
    if (guests.length === 0) return "none"
    const count = guests.filter(pred).length
    return count === 0 ? "none" : count === guests.length ? "all" : "some"
}

const flagColor: Record<FlagState, string> = {
    all: "text-emerald-500",
    some: "text-amber-500",
    none: "text-slate-200",
}

const statusColor: Record<string, string> = {
    created: "bg-orange-50 text-orange-700 border-orange-200",
    paid: "bg-green-50 text-green-700 border-green-200",
    confirmed: "bg-blue-50 text-blue-700 border-blue-200",
    active: "bg-brand/10 text-green-800 border-brand/30",
    completed: "bg-slate-100 text-slate-600 border-slate-200",
    cancelled: "bg-red-50 text-red-600 border-red-200",
}

const paymentColor: Record<string, string> = {
    pending: "text-orange-600",
    paid: "text-green-600",
    refunded: "text-blue-600",
    failed: "text-red-600",
}

interface ReservationTableProps {
    reservations: AdminReservationRow[]
    queryKey: unknown[]
    currency: string
}

export const ReservationTable = ({ reservations, queryKey, currency }: ReservationTableProps) => {
    const { t, i18n } = useTranslation()
    const locale = LOCALE_MAP[i18n.language] ?? "en-US"
    const [showCancelled, setShowCancelled] = useState(false)
    const [selectedReservation, setSelectedReservation] = useState<AdminReservationRow | null>(null)

    const filtered = showCancelled
        ? reservations
        : reservations.filter((r) => r.status !== "cancelled")

    if (reservations.length === 0) {
        return (
            <div className="text-center py-12 text-slate-400 font-bold">
                {t("admin.table.empty")}
            </div>
        )
    }

    return (
        <>
            {/* Show cancelled toggle */}
            <div className="flex items-center justify-end mb-3">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-500 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={showCancelled}
                        onChange={(e) => setShowCancelled(e.target.checked)}
                        className="rounded accent-brand"
                    />
                    {t("admin.table.showCancelled")}
                </label>
            </div>

            <div className="overflow-x-auto rounded-[28px] border border-slate-200">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-500">{t("admin.table.colGuest")}</th>
                            <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-500">{t("admin.table.colDates")}</th>
                            <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-500">{t("admin.table.colSpot")}</th>
                            <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-500">{t("admin.table.colVS")}</th>
                            <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-500">{t("admin.table.colStatus")}</th>
                            <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-500">{t("admin.table.colPayment")}</th>
                            <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-500">{t("admin.table.colFlags")}</th>
                            <th className="text-right px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-500">{t("admin.table.colAmount")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((r, i) => {
                            const primaryGuest = r.guests[0]
                            const guestName = primaryGuest
                                ? `${primaryGuest.firstName} ${primaryGuest.lastName}`
                                : null

                            return (
                                <tr
                                    key={r.id}
                                    onClick={() => setSelectedReservation(r)}
                                    className={cn(
                                        "border-b border-slate-100 last:border-0 cursor-pointer transition-colors",
                                        i % 2 === 0 ? "bg-white hover:bg-slate-50" : "bg-slate-50/50 hover:bg-slate-100/60",
                                        r.status === "cancelled" && "opacity-50",
                                    )}
                                >
                                    <td className="px-4 py-3">
                                        {guestName && (
                                            <p className="font-black text-slate-900">{guestName}</p>
                                        )}
                                        <p className={cn(
                                            "font-bold text-slate-400",
                                            guestName ? "text-xs" : "text-sm text-slate-900",
                                        )}>{r.email}</p>
                                        <p className="text-xs font-bold text-slate-400">
                                            {r.licensePlate} · {t("admin.table.guestCount", { count: r.guestCount })}
                                        </p>
                                    </td>
                                    <td className="px-4 py-3 font-bold text-slate-700">
                                        <p>{formatDate(r.arrivalDate, locale)}</p>
                                        <p className="text-xs text-slate-400">→ {formatDate(r.departureDate, locale)}</p>
                                    </td>
                                    <td className="px-4 py-3 font-black text-slate-700">
                                        {r.spotNumber ? `#${r.spotNumber}` : "—"}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs font-bold text-slate-500">
                                        {r.variableSymbol ?? "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={cn(
                                            "inline-block px-2 py-0.5 rounded-lg border text-xs font-black capitalize",
                                            statusColor[r.status] ?? "bg-slate-100 text-slate-600 border-slate-200",
                                        )}>
                                            {t(`common.status.${r.status}`, { defaultValue: r.status })}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={cn("font-black text-xs capitalize", paymentColor[r.paymentStatus])}>
                                            {t(`common.paymentStatus.${r.paymentStatus}`, { defaultValue: r.paymentStatus })}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        {(() => {
                                            const remit = aggregateFlag(r.guests, (g) => g.remittedToMunicipality === true)
                                            const police = aggregateFlag(r.guests, (g) => g.reportedToForeignPolice === true)
                                            const bvc = aggregateFlag(r.guests, (g) => g.bvcStatus === "preregistered")
                                            const label = (key: string, st: FlagState) =>
                                                `${t(key)}: ${t(`admin.table.flagState.${st}`)}`
                                            return (
                                                <div className="flex items-center gap-2">
                                                    <Building2 className={cn("size-4", flagColor[remit])} aria-label={label("admin.table.flagRemitted", remit)}>
                                                        <title>{label("admin.table.flagRemitted", remit)}</title>
                                                    </Building2>
                                                    <ShieldCheck className={cn("size-4", flagColor[police])} aria-label={label("admin.table.flagForeignPolice", police)}>
                                                        <title>{label("admin.table.flagForeignPolice", police)}</title>
                                                    </ShieldCheck>
                                                    <IdCard className={cn("size-4", flagColor[bvc])} aria-label={label("admin.table.flagBeskydCard", bvc)}>
                                                        <title>{label("admin.table.flagBeskydCard", bvc)}</title>
                                                    </IdCard>
                                                </div>
                                            )
                                        })()}
                                    </td>
                                    <td className="px-4 py-3 text-right font-black text-slate-900">
                                        {formatCurrency(r.totalAmount, currency, locale)}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>

            <ReservationDetailModal
                reservation={selectedReservation}
                onClose={() => setSelectedReservation(null)}
                queryKey={queryKey}
                currency={currency}
            />
        </>
    )
}
